import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from "react";
import styles from "./Select.module.css";
import { ISelectOption } from "../../../types/ISelectOption";
import clsx from "clsx";

type SelectValue = string | number | null;
type SelectSize = "md" | "sm";

interface SelectProps {
  options: ISelectOption[];

  value?: SelectValue;
  defaultValue?: SelectValue;
  onChange?: (value: SelectValue) => void;

  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string | null;

  size?: SelectSize;

  onOpenChange?: (open: boolean) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
}

const Select: FC<SelectProps> = ({
  options,
  value,
  defaultValue = null,
  onChange,
  placeholder = "Select...",
  className,
  disabled = false,
  error,
  size = "md",
  onOpenChange,
  onFocus,
}) => {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const errorId = `${id}-error`;

  const isControlled = value !== undefined;

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<SelectValue>(defaultValue);

  const selectRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement | null>(null);

  const currentValue = isControlled ? value : internalValue;

  const selectedLabel = useMemo(() => {
    if (currentValue === null || currentValue === undefined) return "";
    return options.find((option) => option.value === currentValue)?.label ?? "";
  }, [currentValue, options]);

  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    setOpen((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  };

  const handleSelect = (nextValue: SelectValue, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
    close();
  };

  useEffect(() => {
    if (open && selectedOptionRef.current) {
      selectedOptionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [open]);

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (!selectRef.current) return;
      if (!selectRef.current.contains(event.target as Node)) close();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [close]);

  const hasValue = currentValue !== null && currentValue !== undefined && currentValue !== "";

  return (
    <div
      ref={selectRef}
      className={clsx(
        styles.container,
        size === "sm" && styles.sizeSm,
        className,
      )}
      onClick={(event) => event.stopPropagation()}
      onFocus={onFocus}
      tabIndex={disabled ? -1 : 0}
    >
      <div
        className={clsx(
          styles.trigger,
          disabled && styles.triggerDisabled,
          error && styles.triggerError,
        )}
        onClick={toggle}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
      >
        <span className={clsx(styles.value, !hasValue && styles.placeholder)}>
          {hasValue ? selectedLabel : placeholder}
        </span>

        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M5 8L10 13L15 8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open && !disabled && (
        <div
          id={listboxId}
          className={styles.dropdown}
          role="listbox"
        >
          {options.map((option) => {
            const isSelected = option.value === currentValue;

            return (
              <div
                key={String(option.value)}
                ref={isSelected ? selectedOptionRef : null}
                className={clsx(styles.option, isSelected && styles.optionSelected)}
                onClick={(event) => handleSelect(option.value as SelectValue, event)}
                role="option"
                aria-selected={isSelected}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <span id={errorId} className={styles.errorText} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default Select;
